// Front/back packing for a question-paper print preview.
// One copy is split into two faces. As many copies as fit are stacked on a
// sheet, and the next printed page is the back of that same sheet.

export const A4_FULL_W = 794;
export const A4_FULL_H = 1123;
export const A4_MARGIN_PX = 19;
export const A4_CONTENT_W = A4_FULL_W - A4_MARGIN_PX * 2;
export const A4_CONTENT_H = A4_FULL_H - A4_MARGIN_PX * 2;
// The printed sheet is a few pixels under full A4 so the browser does not
// spill a blank extra page. Packing uses that same shorter content box.
export const A4_PRINT_TRIM = 8;
export const A4_FIT_H = A4_FULL_H - A4_PRINT_TRIM - A4_MARGIN_PX * 2;
export const A4_SCALE = 0.72;
export const DIVIDER_H = 12;

export const FONT_MIN = 8;
export const FONT_MAX = 22;
export const FONT_DEFAULT = 12;
export const LEADING_MIN = 1;
export const LEADING_MAX = 3;
export const LEADING_STEP = 0.1;
export const COPIES_MIN = 1;
export const COPIES_MAX = 200;
export const COPIES_DEFAULT = 1;
const MAX_PER_PAGE = 40;

export function defaultLeading(dir) {
  return dir === 'rtl' ? 2.2 : 1.5;
}

export function paperFontFamily(dir) {
  return dir === 'rtl'
    ? '"Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", "Segoe UI", sans-serif'
    : '"Segoe UI", "Noto Nastaliq Urdu", sans-serif';
}

export function clampFont(value) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return FONT_DEFAULT;
  return Math.max(FONT_MIN, Math.min(FONT_MAX, n));
}

export function clampLeading(value) {
  const n = Math.round(Number(value) * 10) / 10;
  if (!Number.isFinite(n)) return 1.5;
  return Math.max(LEADING_MIN, Math.min(LEADING_MAX, n));
}

export function clampCopies(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return COPIES_MIN;
  return Math.max(COPIES_MIN, Math.min(COPIES_MAX, n));
}

function sum(values) {
  return values.reduce((total, value) => total + (Number(value) || 0), 0);
}

// Cut index is the first block that belongs on the back.
// The header sits on the front only, so the split chases equal face heights
// rather than an equal question count. A section heading travels with the
// questions it introduces.
export function chooseHalfCut(blockHeights, headerHeight, blocks) {
  const list = Array.isArray(blocks) ? blocks : [];
  const heights = Array.isArray(blockHeights) ? blockHeights : [];
  const questions = [];
  list.forEach((block, index) => {
    if (block && block.type === 'question') questions.push(index);
  });
  if (questions.length < 2) return list.length;

  let bestCut = questions[Math.ceil(questions.length / 2)];
  let bestDiff = Infinity;
  const header = Math.max(0, Number(headerHeight) || 0);

  for (let q = 1; q < questions.length; q += 1) {
    let cut = questions[q];
    while (
      cut > 0
      && list[cut - 1]
      && (list[cut - 1].type === 'section' || list[cut - 1].type === 'passage')
      && questions[q - 1] < cut - 1
    ) {
      cut -= 1;
    }
    const frontQuestions = list.slice(0, cut).filter((block) => block && block.type === 'question').length;
    const backQuestions = list.slice(cut).filter((block) => block && block.type === 'question').length;
    if (frontQuestions < 1 || backQuestions < 1) continue;
    const frontH = header + sum(heights.slice(0, cut));
    const backH = sum(heights.slice(cut));
    const diff = Math.abs(frontH - backH);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestCut = cut;
    }
  }
  return bestCut;
}

// How many identical faces fit in one A4 content box. A face taller than the
// page still occupies the sheet so the teacher can shrink the type.
export function copiesThatFit(slotHeight, contentHeight, dividerHeight) {
  const slot = Number(slotHeight) || 0;
  const limit = Number(contentHeight) || 0;
  const divider = Math.max(0, Number(dividerHeight) || 0);
  if (slot <= 0 || limit <= 0) return 1;
  let count = 0;
  let used = 0;
  while (count < MAX_PER_PAGE) {
    const next = used + (count > 0 ? divider : 0) + slot;
    if (next > limit) break;
    used = next;
    count += 1;
  }
  return Math.max(1, count);
}

export function styleKey(fontPt, leading) {
  return `${clampFont(fontPt)}|${clampLeading(leading).toFixed(1)}`;
}

// The shared header style, plus one entry for every sheet that overrides font
// or line spacing. A sheet that overrides only one of them keeps the other
// from the header.
export function collectStyles(fontPt, leading, sheetFont, sheetLeading) {
  const font = clampFont(fontPt);
  const lead = clampLeading(leading);
  const map = new Map();
  const add = (nextFont, nextLead) => {
    const itemFont = clampFont(nextFont);
    const itemLead = clampLeading(nextLead);
    const key = styleKey(itemFont, itemLead);
    if (!map.has(key)) map.set(key, { key, fontPt: itemFont, leading: itemLead });
  };
  add(font, lead);
  const indexes = new Set([
    ...Object.keys(sheetFont || {}),
    ...Object.keys(sheetLeading || {})
  ]);
  indexes.forEach((index) => {
    const ownFont = sheetFont && sheetFont[index] != null ? sheetFont[index] : font;
    const ownLead = sheetLeading && sheetLeading[index] != null ? sheetLeading[index] : lead;
    add(ownFont, ownLead);
  });
  return Array.from(map.values());
}

// Pack copies onto sheets. perPageOf(sheetIndex) is how many copies that
// sheet's own font and line spacing can hold. Sheet 0 keeps its count when
// a later sheet is resized.
export function packSheets(copyCount, perPageOf) {
  const total = Math.max(1, Math.min(COPIES_MAX, parseInt(copyCount, 10) || 1));
  const groups = [];
  let placed = 0;
  let sheet = 0;
  while (placed < total && sheet < total) {
    const perPage = Math.max(1, parseInt(perPageOf(sheet), 10) || 1);
    const count = Math.min(perPage, total - placed);
    const start = placed + 1;
    groups.push({ start, end: placed + count, count, sheet });
    placed += count;
    sheet += 1;
  }
  return groups;
}

export function groupCopies(copyCount, perPage) {
  const total = Math.max(1, Math.min(COPIES_MAX, parseInt(copyCount, 10) || 1));
  const size = Math.max(1, parseInt(perPage, 10) || 1);
  const groups = [];
  for (let start = 1; start <= total; start += size) {
    const end = Math.min(total, start + size - 1);
    groups.push({ start, end, count: end - start + 1 });
  }
  return groups;
}

export const SHEET_CSS = `
.qpp-sheet, .qpp-sheet * { box-sizing: border-box; }
.qpp-sheet { overflow-wrap: break-word; }
.qpp-sheet * { font-family: inherit; }
.qpp-meta {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: baseline;
  gap: 12px;
  font-weight: 700;
  margin: 0 0 6px;
}
.qpp-meta-side { display: flex; gap: 14px; flex-wrap: wrap; min-width: 0; }
.qpp-meta-side--end { justify-content: flex-end; }
.qpp-total { text-align: center; font-weight: 700; white-space: nowrap; }
.qpp-name {
  display: flex;
  justify-content: flex-start;
  align-items: baseline;
  gap: 62px;
  font-weight: 700;
  margin: 0;
}
.qpp-name-gap { height: 10px; }
.qpp-section {
  margin: 0;
  padding: 0.45em 0 0.12em;
  font-size: 1.05em;
  font-weight: 700;
}
.qpp-question { margin: 0; padding: 0 0 0.12em; }
.qpp-passage { margin: 0; padding: 0 0 0.35em; white-space: pre-wrap; }
.qpp-q-label { font-weight: 700; }
.qpp-ul {
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}
.qp-frac {
  display: inline-flex;
  flex-direction: column;
  vertical-align: middle;
  text-align: center;
  margin: 0 0.15em;
  line-height: 1.05;
  font-size: 0.92em;
}
.qp-frac-num, .qp-frac-den { display: block; padding: 0 0.28em; }
.qp-frac-num { border-bottom: 1.5px solid currentColor; }
.qp-sup, .qp-sub { display: inline-block; font-size: 0.72em; line-height: 1.05; }
.qp-sup { vertical-align: super; }
.qp-sub { vertical-align: sub; }
.qp-sqrt { display: inline-flex; align-items: stretch; vertical-align: middle; margin: 0 0.08em; line-height: 1; }
.qp-sqrt-svg { width: 0.72em; height: auto; align-self: stretch; overflow: visible; flex: 0 0 auto; }
.qp-sqrt-body { border-top: 1.5px solid currentColor; padding: 0.06em 0.2em 0.02em 0.08em; line-height: 1.15; }
.qp-matrix { display: inline-flex; align-items: stretch; vertical-align: middle; margin: 0 0.12em; line-height: 1.2; }
.qp-matrix-bracket { width: 0.32em; border-top: 1.5px solid currentColor; border-bottom: 1.5px solid currentColor; border-left: 1.5px solid currentColor; border-radius: 0.4em 0 0 0.4em; margin: 0.06em 0; }
.qp-matrix-bracket--right { border-left: none; border-right: 1.5px solid currentColor; border-radius: 0 0.4em 0.4em 0; }
.qp-matrix-rows { display: inline-flex; flex-direction: column; justify-content: center; padding: 0.08em 0.06em; }
.qp-matrix-row { display: flex; align-items: center; justify-content: center; }
.qp-matrix-cell { display: flex; align-items: center; justify-content: center; min-width: 0.6em; padding: 0.04em 0.38em; text-align: center; white-space: nowrap; }
table.qpp-data { width: auto; border-collapse: collapse; border: 1px solid #111; margin: 0.15em 0 0.45em; color: #111; font-size: 0.95em; white-space: normal; line-height: 1.35; }
table.qpp-data th, table.qpp-data td { border: 1px solid #111; padding: 0.12em 0.7em; text-align: center; font-weight: 600; font-size: inherit; color: #111; background: #fff; text-transform: none; letter-spacing: normal; position: static; white-space: nowrap; vertical-align: middle; }
table.qpp-data tbody tr:last-child th, table.qpp-data tbody tr:last-child td { border-bottom: 1px solid #111; }
table.qpp-data tbody tr:hover { background: transparent; }
table.qpp-data th[scope="row"], table.qpp-data thead th:first-child { text-align: left; background: #f8fafc; }
`;

export function buildQuestionPaperPrintHtml(sheetHtml) {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Question paper</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;600;700&display=swap" rel="stylesheet">
<style>
@page { size: A4; margin: 0; }
html, body { margin: 0; padding: 0; }
body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
${SHEET_CSS}
[data-qp-print-page] { page-break-after: always; break-after: page; background: #fff; }
[data-qp-print-page]:last-child { page-break-after: auto; break-after: auto; }
</style></head><body>${sheetHtml}</body></html>`;
}
